/*
  Towel gift with code.
  While the gift code is applied to the cart (typed into the cart, or via a /discount/CODE link),
  keep exactly one gift towel in it, even if the cart is otherwise empty. Remove the towel when
  the code goes. <towel-colour-picker> lets the customer swap the towel's colour.
  Only lines tagged _gift are managed; towels bought at full price are left alone (the discount
  takes the towel price off once per order).
  Config comes from the script tag (snippets/towel-gift.liquid).
*/
(function () {
  'use strict';

  var script = document.currentScript;
  if (!script) return;

  var CODE = (script.dataset.code || '').toUpperCase();
  // First variant is the default colour; the rest are fallbacks if it sells out.
  var VARIANTS = (script.dataset.variants || '').split(',').map(Number).filter(Boolean);
  if (!CODE || !VARIANTS.length) return;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)|\/discount\//;
  var PREF_KEY = 'd1_towel_colour';
  var queue = Promise.resolve();
  var pendingSync = null;
  var timer;

  function post(path, body) {
    return window.fetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) throw res;
      return res.json();
    });
  }

  function getCart() {
    return window.fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); });
  }

  function giftLines(cart) {
    return cart.items.filter(function (item) {
      return VARIANTS.indexOf(item.variant_id) !== -1 && item.properties && item.properties._gift === CODE;
    });
  }

  function hasCode(cart) {
    return (cart.discount_codes || []).some(function (d) {
      return String(d.code).toUpperCase() === CODE;
    });
  }

  function preferred() {
    try {
      var id = parseInt(localStorage.getItem(PREF_KEY), 10);
      if (VARIANTS.indexOf(id) !== -1) return id;
    } catch (e) {}
    return VARIANTS[0];
  }

  // Try the given variant first, then the rest in order (one may be sold out).
  function addTowel(first) {
    var order = [first].concat(VARIANTS.filter(function (id) { return id !== first; }));
    return order.reduce(function (attempt, id) {
      return attempt.then(function (added) {
        if (added) return added;
        return post('cart/add.js', { items: [{ id: id, quantity: 1, properties: { _gift: CODE } }] })
          .then(function () { return true; }, function () { return false; });
      });
    }, Promise.resolve(false));
  }

  function refreshCartUi() {
    document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
  }

  // Cart writes run one at a time so a colour swap and a sync never interleave.
  function enqueue(task) {
    var run = queue.then(task).then(function (changed) {
      if (changed) refreshCartUi();
    }).catch(function () {});
    queue = run;
    return run;
  }

  function check() {
    return getCart().then(function (cart) {
      var gifts = giftLines(cart);
      var wanted = hasCode(cart);
      var updates = {};

      if (wanted && !gifts.length) return addTowel(preferred());

      gifts.forEach(function (line, i) {
        var qty = wanted && i === 0 ? 1 : 0;
        if (line.quantity !== qty) updates[line.key] = qty;
      });
      if (!Object.keys(updates).length) return false;
      return post('cart/update.js', { updates: updates }).then(function () { return true; });
    });
  }

  // Resolves once the cart is in the right state. Requests made before a queued check starts share it.
  function sync() {
    if (!pendingSync) {
      pendingSync = enqueue(function () {
        pendingSync = null;
        return check();
      });
    }
    return pendingSync;
  }

  function setColour(variantId) {
    try { localStorage.setItem(PREF_KEY, String(variantId)); } catch (e) {}
    return enqueue(function () {
      return getCart().then(function (cart) {
        var current = giftLines(cart)[0];
        if (!current || current.variant_id === variantId) return false;
        return post('cart/add.js', { items: [{ id: variantId, quantity: 1, properties: { _gift: CODE } }] })
          .then(function () {
            var updates = {};
            updates[current.key] = 0;
            return post('cart/update.js', { updates: updates });
          })
          .then(function () { return true; });
      });
    });
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(sync, 300);
  }

  // Re-check after any cart write, whoever makes it (theme, cart app, discount link).
  var nativeFetch = window.fetch;
  window.fetch = function (input) {
    var request = nativeFetch.apply(this, arguments);
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    if (CART_WRITE.test(url)) request.then(schedule, function () {});
    return request;
  };

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (CART_WRITE.test(String(url))) this.addEventListener('loadend', schedule);
    return nativeOpen.apply(this, arguments);
  };

  window.addEventListener('pageshow', function (e) { if (e.persisted) schedule(); });
  window.D1TowelGift = { code: CODE, sync: sync, setColour: setColour };
  sync();

  // Colour swatches, shown only while a gift towel is in the cart (snippets/towel-colour-picker.liquid).
  class TowelColourPicker extends HTMLElement {
    connectedCallback() {
      this.swatches = Array.from(this.querySelectorAll('[data-variant-id]'));
      this.swatches.forEach((btn) => {
        btn.addEventListener('click', () => this.choose(parseInt(btn.dataset.variantId, 10)));
      });
      this.boundLoad = this.load.bind(this);
      document.addEventListener('on:cart:change', this.boundLoad);
      this.load();
    }

    disconnectedCallback() {
      document.removeEventListener('on:cart:change', this.boundLoad);
    }

    load() {
      getCart().then((cart) => {
        var towel = giftLines(cart)[0];
        this.hidden = !towel;
        if (towel) this.select(towel.variant_id);
      }).catch(function () {});
    }

    select(variantId) {
      this.swatches.forEach((btn) => {
        btn.setAttribute('aria-pressed', String(parseInt(btn.dataset.variantId, 10) === variantId));
      });
    }

    choose(variantId) {
      this.select(variantId);
      this.classList.add('is-loading');
      setColour(variantId).then(() => {
        this.classList.remove('is-loading');
        this.load();
      });
    }
  }

  if (!customElements.get('towel-colour-picker')) customElements.define('towel-colour-picker', TowelColourPicker);
})();
